import React, { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { aiApi } from "@/services/api/aiApi";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronUp, AlertTriangle, Info, AlertCircle, Sparkles, Shield, FileSearch, BookOpen, Scale } from "lucide-react";

interface AiDocumentAnalysisProps {
  orderId: number;
  orderDocumentId: number;
  associationId?: number;
  managementCompanyId?: number;
}

const severityConfig = {
  info: { color: "bg-blue-100 text-blue-800 border-blue-200", icon: Info, label: "Info" },
  warning: { color: "bg-amber-100 text-amber-800 border-amber-200", icon: AlertTriangle, label: "Warning" },
  critical: { color: "bg-red-100 text-red-800 border-red-200", icon: AlertCircle, label: "Critical" },
};

const riskScoreConfig = {
  low: "bg-green-100 text-green-800",
  medium: "bg-amber-100 text-amber-800",
  high: "bg-red-100 text-red-800",
};

const analysisSteps = [
  { icon: FileSearch, label: "Reading application data", duration: 2000 },
  { icon: BookOpen, label: "Loading association rules & regulations", duration: 3000 },
  { icon: Scale, label: "Cross-referencing compliance requirements", duration: 5000 },
  { icon: Shield, label: "Generating compliance report", duration: 4000 },
];

const AiProcessingIndicator = () => {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    let elapsed = 0;
    analysisSteps.forEach((step, index) => {
      if (index > 0) {
        elapsed += analysisSteps[index - 1].duration;
        timers.push(setTimeout(() => setActiveStep(index), elapsed));
      }
    });
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="py-4 space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative h-5 w-5">
          <Sparkles className="h-5 w-5 text-violet-500 animate-pulse" />
          <div className="absolute inset-0 h-5 w-5 text-violet-400 animate-ping opacity-30">
            <Sparkles className="h-5 w-5" />
          </div>
        </div>
        <span className="text-sm font-medium text-violet-700">AI analysis in progress</span>
      </div>

      <div className="space-y-1">
        {analysisSteps.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === activeStep;
          const isCompleted = index < activeStep;
          const isPending = index > activeStep;

          return (
            <div
              key={index}
              className={`flex items-center gap-3 rounded-md px-3 py-2 transition-all duration-500 ${
                isActive
                  ? "bg-violet-50 border border-violet-200"
                  : isCompleted
                  ? "opacity-60"
                  : "opacity-30"
              }`}
            >
              <div className="relative flex-shrink-0">
                {isActive ? (
                  <div className="h-4 w-4 rounded-full border-2 border-violet-500 border-t-transparent animate-spin" />
                ) : isCompleted ? (
                  <svg className="h-4 w-4 text-violet-500" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 0a8 8 0 110 16A8 8 0 018 0zm3.78 4.97a.75.75 0 00-1.06 0L7 8.69 5.28 6.97a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.06 0l4.25-4.25a.75.75 0 000-1.06z" />
                  </svg>
                ) : (
                  <Icon className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
              <span
                className={`text-sm ${
                  isActive ? "text-violet-700 font-medium" : isCompleted ? "text-muted-foreground" : "text-muted-foreground"
                }`}
              >
                {step.label}
                {isActive && (
                  <span className="inline-flex ml-1">
                    <span className="animate-[bounce_1.4s_ease-in-out_infinite]">.</span>
                    <span className="animate-[bounce_1.4s_ease-in-out_0.2s_infinite]">.</span>
                    <span className="animate-[bounce_1.4s_ease-in-out_0.4s_infinite]">.</span>
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>

      <div className="h-1.5 bg-violet-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-violet-400 to-violet-600 rounded-full animate-[progress_14s_ease-in-out_forwards]"
          style={{
            animation: "progress 14s ease-in-out forwards",
          }}
        />
      </div>

      <style>{`
        @keyframes progress {
          0% { width: 0%; }
          15% { width: 15%; }
          35% { width: 35%; }
          60% { width: 65%; }
          80% { width: 85%; }
          100% { width: 95%; }
        }
      `}</style>
    </div>
  );
};

export const AiDocumentAnalysis: React.FC<AiDocumentAnalysisProps> = ({
  orderId,
  orderDocumentId,
}) => {
  const queryClient = useQueryClient();
  const [isExpanded, setIsExpanded] = useState(true);

  const { data: analysis, isLoading } = useQuery({
    queryKey: ["ai-analysis", orderDocumentId],
    queryFn: () => aiApi.getOrderDocumentAnalysis(orderDocumentId),
  });

  const analyzeMutation = useMutation({
    mutationFn: () => aiApi.analyzeOrderDocument(orderId, orderDocumentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-analysis", orderDocumentId] });
    },
  });

  const hasAnalysis = analysis && analysis.status === "completed";
  const isProcessing = analysis?.status === "processing" || analyzeMutation.isPending;

  // Auto-trigger analysis if no previous analysis exists
  const autoTriggered = useRef(false);
  useEffect(() => {
    if (!isLoading && !analysis && !autoTriggered.current) {
      autoTriggered.current = true;
      analyzeMutation.mutate();
    }
  }, [isLoading, analysis]);

  return (
    <div className="rounded-lg border bg-card">
      <div
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          {isProcessing ? (
            <div className="relative h-5 w-5">
              <Sparkles className="h-5 w-5 text-violet-500 animate-pulse" />
            </div>
          ) : (
            <Sparkles className="h-5 w-5 text-violet-500" />
          )}
          <h3 className="font-semibold">AI Compliance Analysis</h3>
          {hasAnalysis && analysis.risk_score && (
            <Badge className={riskScoreConfig[analysis.risk_score as keyof typeof riskScoreConfig] || ""}>
              {analysis.risk_score.toUpperCase()} RISK
            </Badge>
          )}
          {isProcessing && (
            <Badge className="bg-violet-100 text-violet-700 border-violet-200 animate-pulse">
              Analyzing
            </Badge>
          )}
          {analysis?.status === "failed" && (
            <Badge variant="destructive">Failed</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isProcessing && hasAnalysis && (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                analyzeMutation.mutate();
              }}
            >
              <Sparkles className="h-3 w-3 mr-1" />
              Re-run Analysis
            </Button>
          )}
          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </div>

      {isExpanded && (
        <div className="border-t px-4 pb-4 pt-3 space-y-4">
          {isLoading && (
            <div className="flex items-center gap-2 text-muted-foreground py-4">
              <div className="h-4 w-4 rounded-full border-2 border-violet-500 border-t-transparent animate-spin" />
              Loading analysis...
            </div>
          )}

          {isProcessing && <AiProcessingIndicator />}

          {analyzeMutation.isError && (
            <div className="text-sm text-red-600 bg-red-50 rounded p-3">
              Analysis failed: {(analyzeMutation.error as Error)?.message || "Unknown error"}
            </div>
          )}

          {analysis?.status === "failed" && !isProcessing && (
            <div className="text-sm text-red-600 bg-red-50 rounded p-3">
              Previous analysis failed: {analysis.error_message || "Unknown error"}
            </div>
          )}

          {hasAnalysis && !isProcessing && (
            <>
              {/* Summary */}
              {analysis.summary && (
                <div className="text-sm text-muted-foreground">
                  {analysis.summary}
                </div>
              )}

              {/* Compliance Issues */}
              {analysis.compliance_issues && analysis.compliance_issues.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Compliance Issues ({analysis.compliance_issues.length})</h4>
                  {analysis.compliance_issues.map((issue: any, index: number) => {
                    const config = severityConfig[issue.severity as keyof typeof severityConfig] || severityConfig.info;
                    const Icon = config.icon;
                    return (
                      <div key={index} className={`rounded-md border p-3 ${config.color}`}>
                        <div className="flex items-start gap-2">
                          <Icon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold uppercase">{config.label}</span>
                              {issue.rule_reference && (
                                <span className="text-xs opacity-75">- {issue.rule_reference}</span>
                              )}
                            </div>
                            <p className="text-sm">{issue.description}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {analysis.compliance_issues?.length === 0 && (
                <div className="text-sm text-green-700 bg-green-50 rounded-md p-3">
                  No compliance issues found.
                </div>
              )}

              {/* Suggestions */}
              {analysis.suggestions && analysis.suggestions.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Suggestions</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                    {analysis.suggestions.map((suggestion: string, index: number) => (
                      <li key={index}>{suggestion}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {!hasAnalysis && !isLoading && !isProcessing && !analysis?.status && (
            <p className="text-sm text-muted-foreground py-2">
              AI analysis will start automatically...
            </p>
          )}
        </div>
      )}
    </div>
  );
};
